"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Apollo Kartele primitives.
 *
 * Recreates the visual atoms from the Claude Design handoff
 * (`.design-handoff/dentex-2/project/apollo-kartele.jsx` + `kartele-shared.jsx`)
 * in our Tailwind v4 + shadcn stack. Every color routes through the warm-neutral
 * design tokens (globals.css) so the per-clinic accent flows through automatically.
 */

/* ── Card ──────────────────────────────────────────────────────────── */

export function KCard({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-border bg-card",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/* ── Section header (title · sub · action) ─────────────────────────── */

export function SectionHead({
  title,
  sub,
  action,
  className,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 border-b border-border px-[22px] py-3.5",
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold leading-tight text-foreground">
          {title}
        </h2>
        {sub != null && (
          <div className="mt-0.5 text-[11.5px] text-text-dim">{sub}</div>
        )}
      </div>
      {action}
    </div>
  );
}

/* ── Chip ──────────────────────────────────────────────────────────── */

type ChipTone = "accent" | "accent-soft" | "soft" | "success";

const CHIP_TONES: Record<ChipTone, string> = {
  accent: "bg-primary text-primary-foreground border-transparent",
  "accent-soft": "bg-accent-tint-2 text-primary border-accent-tint",
  soft: "bg-transparent text-muted-foreground border-border",
  success: "bg-success-tint text-success border-transparent",
};

export function Chip({
  tone = "soft",
  className,
  children,
  ...props
}: React.ComponentProps<"span"> & { tone?: ChipTone }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-[9px] py-[3px] text-[11.5px] font-medium tracking-[0.01em] whitespace-nowrap",
        CHIP_TONES[tone],
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

/* ── Empty state (no-backend sections — never fabricate clinical data) ─ */

export function EmptyState({
  icon,
  title,
  hint,
  action,
  className,
}: {
  icon?: React.ReactNode;
  title: React.ReactNode;
  hint?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border-strong px-4 py-7 text-center",
        className,
      )}
    >
      {icon != null && <div className="text-text-dim">{icon}</div>}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {hint != null && (
        <p className="max-w-sm text-xs text-text-dim">{hint}</p>
      )}
      {action}
    </div>
  );
}

/* ── Scope helpers (single-tooth ↔ arch ↔ full-mouth) ──────────────── */

export type ProcedureScope =
  | "tooth"
  | "teeth"
  | "arch-upper"
  | "arch-lower"
  | "fullmouth"
  | "quadrant-ur"
  | "quadrant-ul"
  | "quadrant-lr"
  | "quadrant-ll";

export const SCOPE_LABEL: Record<ProcedureScope, string> = {
  tooth: "Single tooth",
  teeth: "Multiple teeth",
  "arch-upper": "Upper arch",
  "arch-lower": "Lower arch",
  fullmouth: "Full mouth",
  "quadrant-ur": "Upper right",
  "quadrant-ul": "Upper left",
  "quadrant-lr": "Lower right",
  "quadrant-ll": "Lower left",
};

const QUADRANT_OF = (n: number): "ur" | "ul" | "ll" | "lr" | null => {
  const q = Math.floor(n / 10);
  if (q === 1) return "ur";
  if (q === 2) return "ul";
  if (q === 3) return "ll";
  if (q === 4) return "lr";
  return null;
};

/**
 * Classify a bound-teeth array into a clinical scope. `null`/empty → general
 * (full-mouth) procedure. Single tooth → "tooth"; 2-7 → "teeth"; a whole arch
 * or both → arch/full-mouth; a single quadrant → that quadrant.
 */
export function scopeFromTeeth(
  teeth: number[] | null | undefined,
): ProcedureScope {
  if (!teeth || teeth.length === 0) return "fullmouth";
  if (teeth.length === 1) return "tooth";
  const quadrants = new Set(teeth.map(QUADRANT_OF).filter(Boolean));
  const hasUpper = quadrants.has("ur") || quadrants.has("ul");
  const hasLower = quadrants.has("ll") || quadrants.has("lr");
  if (hasUpper && hasLower) return teeth.length >= 24 ? "fullmouth" : "teeth";
  if (quadrants.size === 1) {
    const only = [...quadrants][0] as "ur" | "ul" | "ll" | "lr";
    return (`quadrant-${only}` as ProcedureScope);
  }
  if (hasUpper) return "arch-upper";
  if (hasLower) return "arch-lower";
  return "teeth";
}

export function isArchScope(scope: ProcedureScope): boolean {
  return (
    scope === "arch-upper" ||
    scope === "arch-lower" ||
    scope === "fullmouth" ||
    scope.startsWith("quadrant-")
  );
}

